import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, MapPin, Globe, Instagram, Facebook, Clock,
  BadgeCheck, Sparkles, ChevronRight, Send, Navigation, Tag, Star, ShieldAlert,
} from "lucide-react";
import { api, resolveImg, track } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Stars } from "@/components/Stars";
import { FavouriteButton } from "@/components/FavouriteButton";
import { PremiumBadge } from "@/components/PremiumBadge";
import { ContactNowModal } from "@/components/ContactNowModal";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];

export default function BusinessProfile({ kind }) {
  const { city, category, slug } = useParams();
  const isService = kind === "service";
  const basePath = isService ? "/services" : "";
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`${isService ? "/service-by-slug" : "/business-by-slug"}/${city}/${category}/${slug}`)
      .then((r) => { setB(r.data); track(r.data.id, "view"); })
      .catch(() => setB(null))
      .finally(() => setLoading(false));
  }, [city, category, slug, isService]);

  useSeo(b ? `${b.name} — ${b.category_name} in ${b.city_name} | OyeBest` : "Business | OyeBest", b?.description);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-10"><CardsSkeleton count={3} /></div>;
  if (!b) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-display text-3xl font-bold">Business not found</h1><Button className="mt-6 rounded-full" onClick={() => navigate("/")}>Go Home</Button></div>;

  const photos = (b.photos || []).filter(Boolean);
  const cover = resolveImg(photos[activePhoto]) || "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=1200";

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 flex-wrap" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-primary">Home</Link><ChevronRight className="w-3.5 h-3.5" />
          {isService && <><Link to="/services" className="hover:text-primary">Services</Link><ChevronRight className="w-3.5 h-3.5" /></>}
          <Link to={`${basePath}/${city}`} className="hover:text-primary">{b.city_name}</Link><ChevronRight className="w-3.5 h-3.5" />
          <Link to={`${basePath}/${city}/${category}`} className="hover:text-primary">{b.category_name}</Link><ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground font-medium">{b.name}</span>
        </nav>

        {/* Gallery */}
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2 rounded-2xl overflow-hidden bg-secondary h-64 md:h-96 relative">
            <img src={cover} alt={b.name} className="w-full h-full object-cover" />
            <FavouriteButton businessId={b.id} className="absolute top-3 right-3 w-11 h-11" />
          </div>
          <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
            {(photos.length ? photos : [photos[0]]).slice(0, 3).map((p, i) => (
              <button key={`${p}-${i}`} onClick={() => setActivePhoto(i)} className={`rounded-2xl overflow-hidden h-24 md:h-[7.4rem] border-2 ${activePhoto === i ? "border-primary" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid lg:grid-cols-3 gap-8">
        {/* Main */}
        <div className="lg:col-span-2">
          {b.is_demo && (
            <div className="mb-4 flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <ShieldAlert className="w-4 h-4" /> Demo / sample listing — for showcase only, not a verified real business.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {b.premium && <PremiumBadge size="lg" />}
            {b.featured && <Badge className="bg-primary text-white gap-1 rounded-full"><Sparkles className="w-3 h-3" /> Featured</Badge>}
            {b.verified && <Badge className="bg-blue-600 text-white gap-1 rounded-full"><BadgeCheck className="w-3 h-3" /> Verified Business</Badge>}
            <Badge variant="outline" className="rounded-full">{b.category_name}</Badge>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{b.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
            <div className="flex items-center gap-2"><Stars rating={b.rating} /><span className="font-bold">{b.rating || "New"}</span><span className="text-sm text-muted-foreground">({b.reviews_count} reviews)</span></div>
            <span className={`flex items-center gap-1 text-sm font-semibold ${b.open_now ? "text-green-600" : "text-red-500"}`}><Clock className="w-4 h-4" /> {b.open_now ? "Open Now" : "Closed"}</span>
            <span className="text-sm font-semibold text-muted-foreground">{b.price_range}</span>
          </div>
          <p className="flex items-start gap-1.5 text-muted-foreground mt-3"><MapPin className="w-4 h-4 mt-1 shrink-0" /> {b.address}</p>
          {b.service_area && <p className="flex items-start gap-1.5 text-muted-foreground mt-1.5"><Navigation className="w-4 h-4 mt-1 shrink-0" /> Service area: {b.service_area}</p>}
          {b.pricing && <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent text-primary px-3 py-1.5 text-sm font-semibold" data-testid="profile-pricing"><Tag className="w-4 h-4" /> {b.pricing}</div>}

          {b.description && <><h2 className="font-display text-xl font-bold mt-8 mb-2">About</h2><p className="text-foreground/80 leading-relaxed">{b.description}</p></>}

          {b.services?.length > 0 && (
            <><h2 className="font-display text-xl font-bold mt-8 mb-3">Services</h2>
              <div className="flex flex-wrap gap-2">{b.services.map((s, i) => <Badge key={`svc-${s}-${i}`} variant="secondary" className="rounded-full px-3 py-1">{s}</Badge>)}</div></>
          )}

          {b.menu?.length > 0 && (
            <><h2 className="font-display text-xl font-bold mt-8 mb-3">Menu</h2>
              <div className="rounded-2xl border border-border divide-y">
                {b.menu.map((m, i) => <div key={`menu-${m.name}-${i}`} className="flex justify-between px-4 py-3"><span>{m.name}</span><span className="font-semibold">{m.price}</span></div>)}
              </div></>
          )}

          {b.offers?.length > 0 && (
            <><h2 className="font-display text-xl font-bold mt-8 mb-3">Offers & Deals</h2>
              <div className="space-y-3">{b.offers.map((o) => (
                <div key={o.id} className="rounded-2xl border border-primary/30 bg-accent p-4 flex items-start gap-3">
                  <Tag className="w-5 h-5 text-primary mt-0.5" />
                  <div><p className="font-bold">{o.title} {o.discount && <span className="text-primary">· {o.discount}</span>}</p><p className="text-sm text-muted-foreground">{o.description}</p></div>
                </div>))}
              </div></>
          )}

          {/* Hours */}
          {b.hours && Object.keys(b.hours).length > 0 && (
            <><h2 className="font-display text-xl font-bold mt-8 mb-3">Opening Hours</h2>
              <div className="rounded-2xl border border-border divide-y max-w-md">
                {DAYS.map(([k, l]) => (
                  <div key={k} className="flex justify-between px-4 py-2.5 text-sm">
                    <span className="text-muted-foreground">{l}</span>
                    <span className="font-medium">{b.hours[k]?.open ? `${b.hours[k].open} – ${b.hours[k].close}` : "Closed"}</span>
                  </div>
                ))}
              </div></>
          )}

          {/* Reviews */}
          <h2 className="font-display text-xl font-bold mt-8 mb-3">Reviews</h2>
          <WriteReview businessId={b.id} />
          {b.reviews?.length > 0 ? (
            <div className="space-y-4">{b.reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border p-4">
                <div className="flex items-center justify-between"><span className="font-semibold">{r.name}</span><Stars rating={r.rating} size="w-3.5 h-3.5" /></div>
                {r.text && <p className="text-sm text-muted-foreground mt-1.5">{r.text}</p>}
              </div>))}
            </div>
          ) : <p className="text-muted-foreground text-sm">No reviews yet. Be the first to review!</p>}
        </div>

        {/* Sticky action sidebar */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-3 rounded-2xl border border-border p-5 bg-white shadow-sm">
            <a href={`tel:${b.phone}`} onClick={() => track(b.id, "call")}>
              <Button className="w-full rounded-full h-12 gap-2 font-semibold text-base" data-testid="profile-call-btn"><Phone className="w-5 h-5" /> Call Now</Button>
            </a>
            <a href={`https://wa.me/91${b.whatsapp}`} target="_blank" rel="noreferrer" onClick={() => track(b.id, "whatsapp")}>
              <Button className="w-full rounded-full h-12 gap-2 bg-green-600 hover:bg-green-700 font-semibold text-base" data-testid="profile-whatsapp-btn"><MessageCircle className="w-5 h-5" /> WhatsApp</Button>
            </a>
            <Button variant="outline" onClick={() => setContactOpen(true)} className="w-full rounded-full h-12 gap-2 font-semibold text-base border-primary/40 text-primary" data-testid="profile-contact-btn"><Send className="w-5 h-5" /> Contact Now</Button>
            <a href={b.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.name + " " + b.address)}`} target="_blank" rel="noreferrer" onClick={() => track(b.id, "directions")}>
              <Button variant="outline" className="w-full rounded-full h-12 gap-2 font-semibold text-base" data-testid="profile-directions-btn"><Navigation className="w-5 h-5" /> Get Directions</Button>
            </a>
            {b.website && (
              <a href={b.website} target="_blank" rel="noreferrer" onClick={() => track(b.id, "website")}>
                <Button variant="outline" className="w-full rounded-full h-12 gap-2 font-semibold text-base" data-testid="profile-website-btn"><Globe className="w-5 h-5" /> Visit Website</Button>
              </a>
            )}
            {(b.instagram || b.facebook) && (
              <div className="flex gap-2 pt-1">
                {b.instagram && <a href={b.instagram} target="_blank" rel="noreferrer" className="flex-1"><Button variant="ghost" className="w-full rounded-full gap-1"><Instagram className="w-4 h-4" /> Instagram</Button></a>}
                {b.facebook && <a href={b.facebook} target="_blank" rel="noreferrer" className="flex-1"><Button variant="ghost" className="w-full rounded-full gap-1"><Facebook className="w-4 h-4" /> Facebook</Button></a>}
              </div>
            )}
            <div className="pt-3 border-t border-border text-center">
              <p className="text-xs text-muted-foreground">Are you the owner?</p>
              <Link to={isService ? "/list-service" : "/list-business"} className="text-sm font-semibold text-primary hover:underline" data-testid="claim-business-link">{isService ? "List / Claim This Service" : "Claim This Business"}</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky action bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border p-2.5 grid grid-cols-4 gap-2 shadow-2xl">
        <a href={`tel:${b.phone}`} onClick={() => track(b.id, "call")}><Button className="w-full rounded-xl h-11 flex-col gap-0 text-[11px]"><Phone className="w-4 h-4" /> Call</Button></a>
        <a href={`https://wa.me/91${b.whatsapp}`} target="_blank" rel="noreferrer" onClick={() => track(b.id, "whatsapp")}><Button className="w-full rounded-xl h-11 flex-col gap-0 text-[11px] bg-green-600 hover:bg-green-700"><MessageCircle className="w-4 h-4" /> WhatsApp</Button></a>
        <Button variant="outline" onClick={() => setContactOpen(true)} className="w-full rounded-xl h-11 flex-col gap-0 text-[11px] border-primary/40 text-primary"><Send className="w-4 h-4" /> Contact</Button>
        <a href={b.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.name)}`} target="_blank" rel="noreferrer" onClick={() => track(b.id, "directions")}><Button variant="outline" className="w-full rounded-xl h-11 flex-col gap-0 text-[11px]"><Navigation className="w-4 h-4" /> Directions</Button></a>
      </div>

      <ContactNowModal open={contactOpen} onOpenChange={setContactOpen} business={b} />
    </>
  );
}

function WriteReview({ businessId }) {
  const { customer, loginWithGoogle } = useCustomerAuth();
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error("Please select a star rating"); return; }
    setBusy(true);
    try {
      await api.post("/customer/reviews", { business_id: businessId, rating, text });
      setSent(true);
      toast.success("Review submitted — pending approval");
    } catch (e) { toast.error("Could not submit review"); }
    finally { setBusy(false); }
  };

  if (!customer) {
    return (
      <div className="rounded-2xl border border-border p-4 mb-4 bg-secondary/40 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-medium">Login to rate and review this business</p>
        <Button variant="outline" className="rounded-full border-primary/40 text-primary" data-testid="review-login-btn"
          onClick={() => { localStorage.setItem("oyebest_post_login", window.location.pathname); loginWithGoogle(); }}>Continue with Google</Button>
      </div>
    );
  }
  if (sent) return <div className="rounded-2xl border border-green-200 bg-green-50 p-4 mb-4 text-sm text-green-700">Dhanyavaad! Your review is pending approval.</div>;

  return (
    <div className="rounded-2xl border border-border p-4 mb-4" data-testid="write-review">
      <p className="font-semibold mb-2">Write a Review</p>
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} onClick={() => setRating(i)} data-testid={`review-star-${i}`}>
            <Star className={`w-7 h-7 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
          </button>
        ))}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Share your experience…" className="rounded-xl" rows={3} data-testid="review-text-input" />
      <Button onClick={submit} disabled={busy} className="rounded-full mt-3 font-semibold" data-testid="review-submit-btn">Submit Review</Button>
    </div>
  );
}
